import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { Button, EmptyState } from '@payaca/ambrosia';
import {
  PinnedTabsContent,
  PinnedTabsTrigger,
  SortableTabs,
  SortableTabsContent,
  SortableTabsHandle,
  SortableTabsItem,
  SortableTabsList,
  SortableTabsTrigger,
} from '@payaca/dnd';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC } from 'react';
import { v4 as uuid } from 'uuid';
import {
  useOptimisticAddEstimateSection,
  useOptimisticRemoveEstimateSection,
  useOptimisticReorderEstimateSections,
} from './api/mutations';
import GrantsTab from './components/GrantsTab';
import SectionEditor from './components/SectionEditor';
import { EstimateSection } from './types';

const GRANTS_TAB_VALUE = 'grants';

type DetailPanelProps = {
  projectId: string;
  estimateId: string;
  activeSectionId: string | null;
  onSetActiveSectionId: (id: string | null) => void;
  onAddLineItem: (sectionId: string) => void;
  onEditLineItem: (lineItemId: string) => void;
};

const DetailPanel: FC<DetailPanelProps> = (props) => {
  const {
    projectId,
    estimateId,
    activeSectionId,
    onSetActiveSectionId,
    onAddLineItem,
    onEditLineItem,
  } = props;

  /**
   * queries
   */
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const sections = estimate?.sections ?? [];

  /**
   * mutations
   */
  const addEstimateSection = useOptimisticAddEstimateSection({
    projectId,
    estimateId,
    options: {
      onMutate: ({ publicId }) => {
        onSetActiveSectionId(publicId);
      },
    },
  });
  const removeEstimateSection = useOptimisticRemoveEstimateSection({
    projectId,
    estimateId,
  });
  const reorderEstimateSections = useOptimisticReorderEstimateSections({
    projectId,
    estimateId,
  });

  /**
   * callbacks
   */
  const handleAdd = () => {
    const publicId = uuid();
    addEstimateSection.mutate({
      estimateId,
      heading: `Section ${sections.length + 1}`,
      publicId,
    });
  };
  const handleSectionDragEnd = (
    _activeItem: EstimateSection,
    _destinationIndex: number,
    items: EstimateSection[]
  ) => {
    reorderEstimateSections.mutate({
      sectionIds: items.map((s) => s.id),
    });
  };
  const handleConfirmDelete = (sectionToDeleteId: string) => {
    if (!sectionToDeleteId) return;
    if (activeSectionId === sectionToDeleteId) {
      const idx = sections.findIndex((s) => s.id === sectionToDeleteId);
      const neighbour = sections[idx + 1] ?? sections[idx - 1];
      onSetActiveSectionId(neighbour?.id ?? null);
    }
    removeEstimateSection.mutate({ id: sectionToDeleteId });
  };

  if (sections.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4">
        <EmptyState
          iconName="link-broken-01.3"
          text="No sections on this estimate"
        />

        <Button size="sm" onClick={handleAdd}>
          Add section
        </Button>
      </div>
    );
  }

  // `activeSectionId` is a section publicId for section tabs or the literal
  // `GRANTS_TAB_VALUE` for the grants tab. Fall back to the first section
  // when the stored value no longer maps to a known tab.
  const activeTabValue =
    activeSectionId === GRANTS_TAB_VALUE
      ? GRANTS_TAB_VALUE
      : (sections.find((s) => s.id === activeSectionId)?.id ?? sections[0].id);

  return (
    <SortableTabs
      value={activeTabValue}
      onValueChange={onSetActiveSectionId}
      items={sections}
      onReorder={handleSectionDragEnd}
      className="min-h-0 flex-1 h-full flex flex-col"
    >
      {(displaySections) => (
        <>
          <div className="flex items-center gap-x-1 p-3.5">
            <SortableTabsList
              aria-label="Estimate sections"
              className="flex gap-x-1 flex-nowrap flex-1 overflow-x-auto"
            >
              {/* Pinned (non-sortable) leading tab — must live inside the
                  Tabs.List that SortableTabsList renders so Radix's
                  RovingFocusGroup picks it up alongside the section
                  triggers. */}
              <PinnedTabsTrigger
                value={GRANTS_TAB_VALUE}
                className={clstx(
                  'whitespace-nowrap inline-flex items-center gap-x-2 rounded-lg px-4 py-3 font-medium focus:outline-none bg-transparent',
                  'text-gray-500 hover:text-green-900 focus:text-green-900',
                  'data-[state=active]:bg-green-700 data-[state=active]:text-white',
                  'data-[state=active]:hover:text-white data-[state=active]:focus:text-white'
                )}
              >
                Grants
              </PinnedTabsTrigger>
              {displaySections.map((section) => (
                <SortableTabsItem
                  key={section.id}
                  value={section.id}
                  className={clstx(
                    'inline-flex items-center gap-x-1 rounded-lg pl-2 font-medium',
                    'data-[state=active]:bg-gray-100'
                  )}
                >
                  <SortableTabsHandle label={section.heading} />
                  <SortableTabsTrigger
                    value={section.id}
                    className={clstx(
                      'whitespace-nowrap inline-flex items-center pr-4 py-3 focus:outline-none bg-transparent cursor-pointer',
                      'text-gray-500 hover:text-blue-900 focus:text-blue-900',
                      'data-[state=active]:text-gray-900',
                      'data-[state=active]:hover:text-gray-900 data-[state=active]:focus:text-gray-900'
                    )}
                  >
                    {section.heading}
                  </SortableTabsTrigger>
                </SortableTabsItem>
              ))}
            </SortableTabsList>

            <div className="bg-white shrink-0 ml-auto">
              <Button
                className="whitespace-nowrap"
                size="sm"
                variant="dashed"
                onClick={handleAdd}
              >
                Add section
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0">
            <PinnedTabsContent
              value={GRANTS_TAB_VALUE}
              className="h-full overflow-y-auto"
            >
              <GrantsTab projectId={projectId} estimateId={estimateId} />
            </PinnedTabsContent>
            {displaySections.map((section) => (
              <SortableTabsContent
                key={section.id}
                value={section.id}
                className="h-full"
              >
                <SectionEditor
                  projectId={projectId}
                  estimateId={estimateId}
                  sectionId={section.id}
                  handleConfirmDelete={handleConfirmDelete}
                  onAddLineItem={onAddLineItem}
                  onEditLineItem={onEditLineItem}
                />
              </SortableTabsContent>
            ))}
          </div>
        </>
      )}
    </SortableTabs>
  );
};

export default DetailPanel;
